// src/waiter/components/TablesSection.js
import React, { useEffect, useRef } from "react";
import { FaCrown } from "react-icons/fa";

/**
 * Sezione Tavoli (mobile friendly)
 * ---------------------------------
 * • Mostra tavoli con stato e cameriere
 * • Corona “take‑over” se la sessione è di un altro cameriere
 * • Aggiorna la lista in realtime su messaggio WebSocket "reload_tables"
 *
 * Props
 * ────────────────────────────────────────────────────────────────────────────
 * tables            : array di tavoli (API /tables)
 * filter, setFilter : stato filtro numero tavolo
 * myUserId          : ID del cameriere loggato
 * onOpenSession(id) : apre una nuova sessione
 * onCloseSession(id): chiude la sessione attiva sul tavolo
 * onEnterTable(t)   : entra nel dettaglio tavolo
 * onTakeOverTable(sessionId, myUserId)
 * reloadTables()    : **callback** che rifà la fetch dei tavoli
 */
export default function TablesSection({
  tables,
  filter,
  setFilter,
  myUserId,
  onOpenSession,
  onCloseSession,
  onEnterTable,
  onTakeOverTable,
  reloadTables,                    // <── NEW
}) {
  /* ───────────────────────── WS reload_tables ─────────────────────────── */
  const wsRef = useRef(null);

  useEffect(() => {
    const wsUrl = `${process.env.REACT_APP_WS_URL}/ws`;
    const ws    = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen    = () => console.log("WS Waiter‑Tables: connesso");
    ws.onmessage = (evt) => {
      if (evt.data === "reload_tables") {
        reloadTables?.();               // ricarica i tavoli
      }
    };
    ws.onclose   = () => console.log("WS Waiter‑Tables: chiuso");

    return () => {
      ws.close();
    };
  }, [reloadTables]);

  /* ───────────────────────── Filtered list ────────────────────────────── */
  const displayedTables = tables.filter((t) =>
    filter ? `${t.table_number}`.includes(filter) : true
  );

  /* ───────────────────────── UI ───────────────────────────────────────── */
  return (
    <div style={styles.container}>
      {/* filtro tavolo */}
      <div style={styles.filterRow}>
        <input
          style={styles.filterInput}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filtra per numero tavolo"
        />
      </div>

      {/* elenco tavoli */}
      <div style={styles.list}>
        {displayedTables.map((t) => {
          const sessionActive = !!t.sessions_active_id;
          const iAmWaiter     = t.current_waiter_id === myUserId;
          const otherWaiter   =
            sessionActive && !iAmWaiter && t.current_waiter_id !== null;

          return (
            <div style={styles.item} key={t.id}>
              {/* HEADER */}
              <div style={styles.itemHeader}>
                <strong style={styles.itemTitle}>Tavolo {t.table_number}</strong>

                {/* Corona “take over” */}
                {otherWaiter && (
                  <FaCrown
                    title="Prendi in carico"
                    style={styles.crownIcon}
                    onClick={() =>
                      onTakeOverTable?.(t.sessions_active_id, myUserId)
                    }
                  />
                )}
              </div>

              {/* cameriere corrente */}
              {sessionActive && (
                <div style={styles.waiterRow}>
                  Cameriere:&nbsp;
                  <span style={styles.waiterName}>
                    {t.current_waiter_username || "—"}
                  </span>
                </div>
              )}

              {/* BOTTONI */}
              {!sessionActive && (
                <button
                  style={styles.actionButton}
                  onClick={() => onOpenSession(t.id)}
                >
                  Apri Sessione
                </button>
              )}

              {sessionActive && (
                <>
                  <button
                    style={styles.enterButton}
                    onClick={() => onEnterTable(t)}
                  >
                    Entra
                  </button>

                  {/* “Chiudi” solo se sono io il cameriere */}
                  {iAmWaiter && (
                    <button
                      style={styles.actionButton}
                      onClick={() => onCloseSession(t.id)}
                    >
                      Chiudi Sessione
                    </button>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ───────────────────────── STILI (immutati, + crownIcon) ─────────────── */
const palette = {
  primary:   "#FF7F00",
  secondary: "#FF6600",
  background:"#FAFAFA",
  card:      "#FFFFFF",
  textDark:  "#333333",
  textLight: "#FFFFFF",
  green:     "#2ECC71",
};

const styles = {
  container:{ width:"100%", display:"flex", flexDirection:"column", gap:10 },
  filterRow:{ marginBottom:10, width:"100%" },
  filterInput:{
    width:"80%", maxWidth:280, padding:8, fontSize:14,
    borderRadius:6, border:"1px solid #ccc", boxSizing:"border-box",
  },
  list:{ display:"flex", flexDirection:"column", gap:10 },
  item:{
    backgroundColor:palette.card, border:"1px solid #ddd", borderRadius:6,
    padding:10, boxShadow:"0 1px 3px rgba(0,0,0,0.1)",
  },
  itemHeader:{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 },
  itemTitle:{ fontSize:16, color:palette.textDark },
  crownIcon:{ fontSize:20, color:"#d4af37", cursor:"pointer" },
  waiterRow:{ fontSize:14, marginBottom:8, color:palette.textDark },
  waiterName:{ fontWeight:"bold", color:palette.primary },
  actionButton:{
    marginTop:5, marginRight:5,
    backgroundColor:palette.secondary, color:palette.textLight,
    border:"none", padding:"10px 14px", cursor:"pointer",
    borderRadius:6, fontSize:14, fontWeight:"bold",
  },
  enterButton:{
    marginTop:5, marginRight:5,
    backgroundColor:palette.green, color:palette.textLight,
    border:"none", padding:"10px 14px", cursor:"pointer",
    borderRadius:6, fontSize:14, fontWeight:"bold",
  },
};
